import FormField from '@components/Common/FormField';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
// We'll dynamically import JSZip when needed so the package is optional at runtime
import RecordLink from '@components/Common/RecordLink';
import MintlyTable from '@components/Common/Table';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import FileUpload from '@components/Common/FileUpload/FileUpload';
import FilterRow from '@components/Common/FilterRow';
import { PageHero } from '@components/Common/Panel';
import { SectionCard } from '@components/Common/EntityWorkspace';
import { useToast } from '@components/Common/Toast/ToastProvider';
import BulkResumeUpload from './BulkResumeUpload';
import { uploadToS3Direct } from '@components/Common/directUpload';
import { createResumeUploadMutation, listResumeUploadsByFileNameQuery, listResumeUploadsQuery, UploadStatus } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import './index.scss';
import { RESUME_BUCKET, resolveResumeUrl, resumeObjectUrl } from '@components/Common/resumeUrl';
import ProfileBackfillCard from '@components/JobSeekers/ProfileBackfillCard';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { runBulk } from './bulkProcess';
import type { BulkProgress } from './bulkProcess';
import { ALLOWED_EXT, extractFilesFromZip } from './zipEntries';
import { formatDateTime as formatDateTimeShared } from '@helpers/date';

/* "All" is a tile rather than a separate control, because clearing the filter is the same kind of
   act as setting it and used to live in a different widget. */
const PIPELINE_TILES: { label: string; key: string; color: string }[] = [
    { label: 'All uploads', key: 'ALL', color: 'var(--tx-hi)' },
    { label: 'Pending', key: UploadStatus.PENDING, color: 'var(--amber)' },
    { label: 'Processing', key: UploadStatus.PROCESSING, color: 'var(--acc)' },
    { label: 'Created', key: UploadStatus.SUCCESS, color: 'var(--green)' },
    { label: 'Replaced existing', key: UploadStatus.REPLACED, color: 'var(--green)' },
    { label: 'Needs email', key: UploadStatus.NEEDS_EMAIL, color: 'var(--amber)' },
    { label: 'Already a user', key: UploadStatus.REJECTED_EXISTING_USER, color: 'var(--tx-low)' },
    { label: 'Failed', key: UploadStatus.FAILED, color: 'var(--red)' },
];

const BUCKET_NAME = RESUME_BUCKET;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB per resume

/**
 * Upper bound on one bulk run.
 *
 * The list query is paginated and this asks for a single page, so it needs a size. 500 is well
 * above any backlog seen here (the largest month was 238 uploads in total) and low enough that a
 * runaway queue cannot start a run nobody can sit through.
 */
const BULK_PROCESS_CAP = 500;


// Small helper to guess MIME type from filename extension. Add more types if needed.
const getMimeTypeFromName = (name: string) => {
    const n = name.toLowerCase();
    if (n.endsWith('.pdf')) return 'application/pdf';
    if (n.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    return '';
};

/**
 * Download a résumé. Returns false when it could not be reached, so the caller can say so.
 *
 * Module scope, which is why it reports rather than toasts: the toast hook lives in the component.
 */
const handleResumeClick = async (resumeUrl?: string | null): Promise<boolean> => {
    if (!resumeUrl) return false;
    // The stored URL is public-form but the object is private, so fetching it directly is a 403.
    // resolveResumeUrl signs for it through the BFF; see Common/resumeUrl.ts.
    const signed = await resolveResumeUrl(resumeUrl);
    if (!signed) return false;
    try {
        const resp = await axios.get(signed, { responseType: 'blob' });
        const blob = resp.data;
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const fileName = decodeURIComponent(resumeUrl.split('/').pop() || 'resume');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
        return true;
    } catch (err) {
        // Fall back to letting the browser fetch it, still via the signed URL.
        window.open(signed, '_blank');
        return true;
    }
};

// Format status string to title case (PENDING → Pending)
/**
 * Labels for the upload statuses.
 *
 * The generic fallback lowercases everything after the first letter, which turns NEEDS_EMAIL into
 * "Needs_email". These say what actually happened to the candidate, which is the useful thing on a
 * row an operator has to act on.
 */
const STATUS_LABELS: Record<string, string> = {
    [UploadStatus.PENDING]: 'Pending',
    [UploadStatus.PROCESSING]: 'Processing',
    [UploadStatus.SUCCESS]: 'Created',
    [UploadStatus.FAILED]: 'Failed',
    [UploadStatus.NEEDS_EMAIL]: 'Needs email',
    [UploadStatus.REPLACED]: 'Replaced existing',
    [UploadStatus.REJECTED_EXISTING_USER]: 'Already a user',
};

const formatStatus = (status?: string | null): string => {
    if (!status) return '-';
    return STATUS_LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

// Format a UTC datetime string to the browser's local timezone
const formatDateTime = (dt?: string | null): string => {
    return formatDateTimeShared(dt);
};

// Format bytes into human-friendly string (B, KB, MB)
const formatFileSize = (bytes?: number | null) => {
    if (bytes === null || bytes === undefined) return '-';
    if (bytes === 0) return '0 B';
    const abs = Math.abs(bytes);
    if (abs < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
};


/**
 * Put a file in the bucket. Returns nothing, deliberately.
 *
 * This used to `return resp.data?.url || ''`, which reads as "gives you the URL" and never did:
 * the BFF responds `{ message: 'File uploaded successfully' }` with no url field, so it always
 * returned an empty string. The single and zip paths both construct the URL themselves and ignore
 * the return, so they work; the one caller that trusted it wrote `fileUrl: ''` and the backend
 * then failed the record with "No fileUrl on resume upload record".
 *
 * Returning void means the next caller cannot make that mistake.
 */
// Direct-to-S3 presigned POST; returns the server-composed key the record should store. The
// single and zip paths call this the same way the multi-select does through uploadToS3Direct.
const uploadFileToS3 = async (bucketName: string, file: File, fileName: string): Promise<string> =>
    uploadToS3Direct(bucketName, file, { fileName });

/** A `?status=` value the tiles know, else All. An unknown value must not select an empty tab. */
export const initialStatus = (param: string | null): string => {
    const v = (param ?? '').trim().toUpperCase();
    return (Object.values(UploadStatus) as string[]).includes(v) ? v : 'ALL';
};

const JobSeekersImportPage: React.FC = () => {
    const superAdmin = isSuperAdmin(useCurrentUserRole());
    const { showToast } = useToast();
    const [singleFile, setSingleFile] = useState<File | null>(null);
    const [bulkFile, setBulkFile] = useState<File | null>(null);
    const [records, setRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [polling] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [duplicateFileName, setDuplicateFileName] = useState<string | null>(null);
    const [bulkDuplicateDialogOpen, setBulkDuplicateDialogOpen] = useState(false);
    const [bulkDuplicateFiles, setBulkDuplicateFiles] = useState<string[]>([]);
    const [bulkExtractedFiles, setBulkExtractedFiles] = useState<{ name: string; buffer: ArrayBuffer }[]>([]);
    const [page, setPage] = useState(0);
    // Fixed page size: there has never been a control to change it.
    const rowsPerPage = 5;
    const [totalElements, setTotalElements] = useState<number>(0);
    // `?status=FAILED` opens on one status: the dashboard's failed-imports tile links here.
    const [searchParams] = useSearchParams();
    const [selectedStatus, setSelectedStatus] = useState<string>(() => initialStatus(searchParams.get('status')));
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
    const [bulkProgress, setBulkProgress] = useState<BulkProgress | null>(null);
    const [bulkConfirm, setBulkConfirm] = useState<string[] | null>(null);
    // A ref, not state: the loop reads it between rows and a state update would not be visible
    // to a closure already running.
    const stopBulkRef = useRef(false);

    const createResumeRecord = useCallback(async (payload: any) => {
        const mutation = createResumeUploadMutation(payload);
        const { data } = await fetchGraphQLApiData(mutation);
        return data?.createResumeUpload || null;
    }, []);

    // Unified drop handler: centers upload card will call this when a file is dropped.
    const handleUnifiedDrop = (f: File) => {
        const name = f.name.toLowerCase();
        if (name.endsWith('.zip') || f.type === 'application/zip') {
            setBulkFile(f);
        } else if (ALLOWED_EXT.some(ext => name.endsWith(ext))) {
            setSingleFile(f);
        } else {
            showToast('Unsupported file type. Upload a .pdf, .docx or .zip.', 'error');
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    // No rows-per-page control: the old TablePagination offered rowsPerPageOptions={[5]}, a single
    // option, so the size was never actually changeable. MintlyTable's pager takes the page only.

    // Accept an optional file param so we can start upload immediately after a drop
    const handleSingleUpload = async (fileParam?: File) => {
        const fileToUse = fileParam ?? singleFile;
        if (!fileToUse) return;
        if (fileToUse.size > MAX_FILE_SIZE) {
            showToast('That file is over the 10MB limit.', 'error');
            return;
        }
        // Check for existing upload with same file name
        try {
            const q = listResumeUploadsByFileNameQuery(fileToUse.name);
            const { data: existData } = await fetchGraphQLApiData(q);
            if (existData.getResumeUploadByFileName) {
                // Open a dialog to let the user cancel or proceed anyway
                setDuplicateFileName(fileToUse.name);
                setDuplicateDialogOpen(true);
                return;
            }
        } catch (err) {
            console.warn('Failed to check existing uploads for', fileToUse.name, err);
        }
        const timestamp = Date.now();
        const fileName = `external_${timestamp}_${fileToUse.name}`;
        const resumeUrl = resumeObjectUrl(fileName);
        setLoading(true);
        try {
            await doSingleUpload(fileToUse, fileName, resumeUrl);
        } finally {
            setLoading(false);
        }
    };

    const doSingleUpload = async (file: File, bucketFileName: string, _resumeUrl: string) => {
        try {
            // The server's key, not the name we asked with; the stored URL is built from it.
            const key = await uploadFileToS3(BUCKET_NAME, file, bucketFileName);
            const payload = {
                fileName: file.name,
                fileType: file.type,
                fileSize: file.size,
                fileUrl: resumeObjectUrl(key),
                bucketKey: key,
                contentHash: '',
                createdBy: 'ui',
            };
            await createResumeRecord(payload);
            setSingleFile(null);
            setPage(0);
            setRefreshKey(k => k + 1);
        } catch (err) {
            console.error(err);
            showToast('Upload failed. Nothing was saved.', 'error');
        }
    };

    // Accept an optional file param so we can start upload immediately after a drop
    const handleBulkUpload = async (fileParam?: File) => {
        const fileToUse = fileParam ?? bulkFile;
        if (!fileToUse) return;
        setLoading(true);
        try {
            if (fileToUse.type === 'application/zip' || fileToUse.name.toLowerCase().endsWith('.zip')) {
                // Try to extract client-side
                const extracted = await extractFilesFromZip(fileToUse);
                if (!extracted.length) {
                    showToast('That zip has no .pdf or .docx inside it.', 'warning');
                    return;
                }
                // First pass: check for duplicates so we can ask the user what to do
                const duplicates: string[] = [];
                for (const f of extracted) {
                    try {
                        const q = listResumeUploadsByFileNameQuery(f.name);
                        const { data: existData } = await fetchGraphQLApiData(q);
                        if (existData.getResumeUploadByFileName) duplicates.push(f.name);
                    } catch (err) {
                        console.warn('Failed to check existing uploads for', f.name, err);
                    }
                }

                if (duplicates.length > 0) {
                    // Save extracted files and show dialog to let user choose behavior
                    setBulkExtractedFiles(extracted);
                    setBulkDuplicateFiles(duplicates);
                    setBulkDuplicateDialogOpen(true);
                    return;
                }

                // No duplicates found; proceed with upload of all extracted files
                await doBulkUpload(extracted, false);
            } else {
                /*
                 * Unreachable: handleUnifiedDrop only ever puts a .zip into bulkFile, and the
                 * Upload button routes anything else to handleSingleUpload. Kept as an explicit
                 * refusal rather than the code that used to be here, which uploaded the file and
                 * then set `fileUrl` from uploadFileToS3's return value — always '' — so the
                 * backend failed the record with "No fileUrl on resume upload record". If bulk
                 * ever accepts loose files, build the URL the way the zip branch does rather than
                 * trusting the upload helper.
                 */
                showToast('Bulk upload expects a .zip. Drop a .pdf or .docx to import one CV.', 'error');
                return;
            }
            setBulkFile(null);
            fetchRecords();
        } catch (err) {
            console.error(err);
            showToast('Bulk upload failed. Nothing was saved.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const doBulkUpload = async (extracted: { name: string; buffer: ArrayBuffer }[], skipDuplicates: boolean) => {
        const skippedFiles: string[] = [];
        for (const f of extracted) {
            // If skipping duplicates, re-check existence and skip if present
            if (skipDuplicates) {
                try {
                    const q = listResumeUploadsByFileNameQuery(f.name);
                    const { data: existData } = await fetchGraphQLApiData(q);
                    if (existData.getResumeUploadByFileName) {
                        skippedFiles.push(`${f.name} (duplicate)`);
                        continue;
                    }
                } catch (err) {
                    console.warn('Failed to check existing uploads for', f.name, err);
                }
            }

            const sizeBytes = (f.buffer && (f.buffer as ArrayBuffer).byteLength) || 0;
            if (sizeBytes > MAX_FILE_SIZE) {
                skippedFiles.push(`${f.name} (too large)`);
                continue;
            }

            const mime = getMimeTypeFromName(f.name);
            const blob = new Blob([f.buffer], { type: mime || undefined });
            const fileObj = new File([blob], f.name, { type: mime || '' });

            const bucketFileName = `external_${Date.now()}_${f.name}`;

            try {
                const key = await uploadFileToS3(BUCKET_NAME, fileObj, bucketFileName);
                const payload = {
                    fileName: f.name,
                    fileType: fileObj.type || '',
                    fileSize: fileObj.size,
                    fileUrl: resumeObjectUrl(key),
                    bucketKey: key,
                    contentHash: '',
                    createdBy: 'ui',
                };
                await createResumeRecord(payload);
            } catch (err) {
                console.error('Failed to upload', f.name, err);
                skippedFiles.push(`${f.name} (error)`);
            }
        }
        setPage(0);
        setRefreshKey(k => k + 1);
    };

    /**
     * Mark one row PROCESSING, then ask the server to generate the job seeker.
     *
     * Extracted so the single-row button and the bulk run drive the same two mutations in the same
     * order. The handshake is not optional: generateJobSeekerFromResume returns the record untouched
     * unless it is already PROCESSING, so a bulk loop that skipped markProcessing would report
     * success for rows it had not touched.
     *
     * Returns rather than toasts, because 29 toasts is not a progress report.
     */
    const runProcessing = async (recordId: string): Promise<'created' | 'no-seeker' | 'failed'> => {
        const markMutation = `mutation { markProcessing(recordId: \"${recordId}\"){ _id status } }`;
        const { data: markData } = await fetchGraphQLApiData(markMutation);
        if (!markData?.markProcessing) return 'failed';

        const mutation = `mutation { generateJobSeekerFromResume(recordId: \"${recordId}\"){ _id } }`;
        const { data } = await fetchGraphQLApiData(mutation);
        return data?.generateJobSeekerFromResume ? 'created' : 'no-seeker';
    };

    /**
     * Every PENDING id, not just the page on screen.
     *
     * Deliberately unfiltered by date, so the button matches the Pending tile beside it: the tiles
     * count every status across all time, and a button that silently meant "today's pending" while
     * the tile said 29 would be read as broken.
     */
    const fetchAllPendingIds = async (): Promise<string[]> => {
        const q = listResumeUploadsQuery(UploadStatus.PENDING, undefined, undefined, 0, BULK_PROCESS_CAP);
        const { data } = await fetchGraphQLApiData(q);
        return (data?.listResumeUploads?.content ?? [])
            .map((r: any) => r?._id)
            .filter(Boolean);
    };

    /** Drive the queue and report what happened. The loop itself is in `bulkProcess`. */
    const processAllPending = async (ids: string[]) => {
        stopBulkRef.current = false;
        setBulkProgress({ done: 0, total: ids.length, created: 0, failed: 0 });

        const result = await runBulk(ids, runProcessing, {
            onProgress: setBulkProgress,
            shouldStop: () => stopBulkRef.current,
            onError: (id, err) => console.error('Bulk processing failed for', id, err),
        });

        setBulkProgress(null);
        await fetchStatusCounts();
        await fetchRecords();
        showToast(
            `${result.stopped ? 'Stopped. ' : ''}${result.created} created, ${result.failed} failed.`,
            result.failed && !result.created ? 'error' : 'success',
        );
    };

    // Trigger server-side job to process a resume into a JobSeeker record.
    const processNow = async (recordId?: string) => {
        if (!recordId) {
            showToast('This record has no id yet, so it cannot be processed.', 'error');
            return;
        }
        // mark as processing
        setProcessingIds(prev => {
            const n = new Set(prev);
            n.add(recordId);
            return n;
        });
        try {
            // Optimistically update local UI: mark the row as PROCESSING and adjust counts
            setRecords(prev => prev.map(r => (r._id === recordId ? { ...r, status: 'PROCESSING' } : r)));
            setStatusCounts(prev => {
                const next = { ...(prev || {}) } as Record<string, number>;
                // decrement PENDING if present and increment PROCESSING
                next[UploadStatus.PENDING] = Math.max(0, (next[UploadStatus.PENDING] || 0) - 1);
                next['PROCESSING'] = (next['PROCESSING'] || 0) + 1;
                return next;
            });

            // No refetch here. It used to sit between the two mutations, to make the row leave the
            // PENDING list at once; the optimistic update above already does that, and now that
            // runProcessing owns both mutations a refetch here would read the row back as PENDING
            // and undo it.
            const outcome = await runProcessing(recordId);
            if (outcome === 'failed') {
                showToast('Could not start processing. Try again.', 'error');
            } else if (outcome === 'no-seeker') {
                showToast('Processing finished but produced no job seeker.', 'warning');
            } else {
                showToast('Processed. Job seeker created.', 'success');
            }

            // refresh current list and counts since status may have changed further
            await fetchStatusCounts();
            await fetchRecords();
        } catch (err) {
            console.error('Failed to process resume', err);
            showToast('Processing failed. The detail is in the console.', 'error');
        } finally {
            setProcessingIds(prev => {
                const n = new Set(prev);
                n.delete(recordId);
                return n;
            });
        }
    };

    const fetchRecords = async () => {
        try {
            const statusFilter = selectedStatus === 'ALL' ? undefined : selectedStatus;
            const startOfDay = selectedDate ? new Date(`${selectedDate}T00:00:00.000Z`).toISOString() : undefined;
            const endOfDay = selectedDate ? new Date(`${selectedDate}T23:59:59.999Z`).toISOString() : undefined;
            const q = listResumeUploadsQuery(statusFilter, startOfDay, endOfDay, page, rowsPerPage);
            const { data } = await fetchGraphQLApiData(q);
            const pageResp = data?.listResumeUploads || null;
            const list = pageResp?.content || [];
            setRecords(list);
            const total = pageResp?.totalElements;
            const coercedTotal = typeof total === 'string' ? parseInt(total, 10) || list.length : (total ?? list.length);
            setTotalElements(coercedTotal);
        } catch (err) {
            console.error('Failed to fetch records', err);
        }
    };



    useEffect(() => {
        const refresh = async () => {
            setLoading(true);
            await fetchStatusCounts();
            await fetchRecords();
            setLoading(false);
        };
        refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatus, selectedDate, page, rowsPerPage, refreshKey]);

    const fetchStatusCounts = async () => {
        try {
            const statuses = Object.values(UploadStatus) as string[];
            const counts: Record<string, number> = {};
            await Promise.all(statuses.map(async (s) => {
                try {
                    const q = listResumeUploadsQuery(s, undefined, undefined, 0, 1);
                    const { data } = await fetchGraphQLApiData(q);
                    const total = data?.listResumeUploads?.totalElements;
                    counts[s] = typeof total === 'string' ? parseInt(total, 10) || 0 : (total ?? 0);
                } catch (err) {
                    console.warn('Failed to fetch count for status', s, err);
                    counts[s] = 0;
                }
            }));
            setStatusCounts(counts);
        } catch (err) {
            console.error('Failed to fetch status counts', err);
        }
    };

    useEffect(() => {
        let t: any;
        if (polling) {
            t = setInterval(fetchRecords, 5000);
        }
        return () => clearInterval(t);
    }, [polling]);

    // Records returned from server are already paginated for the selected status.
    const displayRecords = records;
    // From the tile counts, which are unfiltered by date -- so the button's number and the
    // Pending tile's number are the same number.
    const pendingCount = statusCounts[UploadStatus.PENDING] ?? 0;

    return (
        <Box className="cc-create jobseekers-import-page">
            <PageHero
                kicker="Job seekers"
                title="Import candidates from resumes."
                text="Drop one CV or a zip of them. Each upload becomes a record here, and the pipeline turns it into a job seeker."
                actions={(
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRecords}>
                        Refresh
                    </Button>
                )}
            />

            <Box className="jobseekers-import-page__top">
                <SectionCard title="Upload resume (PDF / DOCX / ZIP)">
                    {/* One full-row cell, not three.
                        SectionCard's body is `repeat(auto-fit, minmax(190px, 1fr))`, so every direct
                        child takes a track: the drop zone, its caption and the action row were laid
                        out as three 190px columns side by side. Nothing here is a field, so nothing
                        here should be a column. */}
                    <Box className="cc-field--full">
                        <FileUpload
                            disabled={loading}
                            onFileDrop={(f) => handleUnifiedDrop(f)}
                            file={singleFile ?? bulkFile ?? ''}
                            supportedFileTypes={[...ALLOWED_EXT, '.zip'].join(',')}
                            maxFileSize={MAX_FILE_SIZE * 10}
                            title="Drop or select a file"
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                            Drop a <strong>.pdf</strong> or <strong>.docx</strong> to create a single record, or a <strong>.zip</strong> to upload many at once. Records are processed automatically within 24 hours.
                        </Typography>
                        <div className="upload-actions">
                            {(singleFile || bulkFile) ? (
                                <>
                                    <Typography variant="body2">{(singleFile ?? bulkFile)?.name}</Typography>
                                    <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => {
                                        if (singleFile) handleSingleUpload(singleFile);
                                        else if (bulkFile) handleBulkUpload(bulkFile);
                                    }} disabled={loading}>
                                        Upload
                                    </Button>
                                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={() => { setSingleFile(null); setBulkFile(null); }} disabled={loading}>Remove</Button>
                                </>
                            ) : (
                                <Typography variant="body2" color="textSecondary">No file selected.</Typography>
                            )}
                        </div>
                    </Box>

                    {/* Direct multi-select: pick up to 50 CVs at once, no zipping, each straight to
                        S3 via a presigned POST. The single/zip path above still works; this is the
                        friendlier entry point HRs asked for, and the one that keeps the bytes off
                        our server. */}
                    <Box className="cc-field--full">
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            Or pick multiple files directly, no zip needed:
                        </Typography>
                        <BulkResumeUpload
                            disabled={loading}
                            onComplete={() => setRefreshKey((k) => k + 1)}
                        />
                    </Box>
                </SectionCard>

                {/*
                    The pipeline tiles are the page's only status filter now.

                    There were two, both writing `selectedStatus`: these tiles and a row of chips
                    below, with the same seven statuses and the same counts. CLAUDE.md's rule is one
                    filter mechanism per page, and a reader had no way to tell whether the two were
                    independent, so a tile click that visibly changed a chip read as a bug.
                    The tiles won because they answer the question first ("what is stuck?") and
                    filter second; the chips only filtered.
                */}
                <SectionCard title="Import pipeline">
                    <Box className="jobseekers-import-page__tiles">
                    {PIPELINE_TILES.map(({ label, key, color }) => (
                        <Box
                            key={key}
                            /* The shared stat surface by class rather than by <Panel>, the same way
                               EmailAnalytics and EmailAudit reach for `cc-panel--panel`. Panel
                               renders either a Box or a router Link and takes neither a click
                               handler nor an sx, and this tile filters the table below rather than
                               navigating, so it keeps its own behaviour and borrows only the
                               surface — which is the part that had drifted into eight variants. */
                            className="cc-panel cc-panel--stat import-stat-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => { setSelectedStatus(key); setPage(0); }}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setSelectedStatus(key);
                                    setPage(0);
                                }
                            }}
                            sx={{ borderColor: selectedStatus === key ? color : 'var(--color-border-light)' }}
                        >
                            <Typography className="import-stat-number" sx={{ color }}>
                                {key === 'ALL'
                                    ? Object.values(statusCounts).reduce((a, b) => a + b, 0)
                                    : statusCounts[key] ?? 0}
                            </Typography>
                            <Typography className="import-stat-label" variant="caption">{label}</Typography>
                        </Box>
                    ))}
                    </Box>
                </SectionCard>
            </Box>

            {/* Super Admin only: reads CVs through OpenAI and writes to candidate records in bulk. */}
            {superAdmin && <ProfileBackfillCard />}

            {/* Resume tracking table */}
            <SectionCard title="Resume tracking">
                {/* Date only. The status half of this row was the second filter mechanism and now
                    lives in the pipeline tiles above. */}
                <FilterRow className="cc-field--full">
                    <FormField
                      required={false}
                        name="import-date"
                        label="Date"
                        type="date"
                        value={selectedDate}
                        onChange={(_n, v) => { setSelectedDate(String(v)); setPage(0); }}
                    />
                    <Button size="small" variant="outlined" onClick={() => { setSelectedDate(new Date().toISOString().slice(0, 10)); setPage(0); }}>Today</Button>
                    <Button size="small" variant="text" onClick={() => { setSelectedDate(''); setPage(0); }}>All Dates</Button>
                    {bulkProgress ? (
                        <>
                            <span className="import-bulk__progress">
                                Processing {bulkProgress.done} of {bulkProgress.total}
                                {bulkProgress.failed > 0 ? ` — ${bulkProgress.failed} failed` : ''}
                            </span>
                            {/* Stop, not cancel: the row in flight finishes either way. */}
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => { stopBulkRef.current = true; }}
                            >
                                Stop
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            disabled={loading || !pendingCount}
                            onClick={async () => {
                                const ids = await fetchAllPendingIds();
                                if (!ids.length) {
                                    showToast('There is nothing pending to process.', 'info');
                                    return;
                                }
                                setBulkConfirm(ids);
                            }}
                        >
                            {pendingCount ? `Process all pending (${pendingCount})` : 'Process all pending'}
                        </Button>
                    )}
                </FilterRow>

                {/* Bulk process confirmation */}
                <ConfirmDialog
                    open={Boolean(bulkConfirm)}
                    title="Process every pending resume?"
                    body={
                        <>
                            This runs <strong>{bulkConfirm?.length ?? 0}</strong> resumes through
                            extraction one at a time, and each one calls an LLM, so it takes a while.
                            Every candidate it reads is created for real. You can stop partway and
                            keep whatever finished.
                        </>
                    }
                    actionLabel="Process them"
                    onConfirm={() => {
                        const ids = bulkConfirm ?? [];
                        setBulkConfirm(null);
                        void processAllPending(ids);
                    }}
                    onCancel={() => setBulkConfirm(null)}
                />

                {/* Duplicate file dialog */}
                <ConfirmDialog
                    open={duplicateDialogOpen}
                    title="That file has been uploaded before"
                    body={
                        <>
                            A file named <strong>{duplicateFileName}</strong> is already in the
                            bucket. Uploading again creates a second record for the same CV.
                        </>
                    }
                    actionLabel="Upload anyway"
                    busy={loading}
                    onConfirm={async () => {
                        setDuplicateDialogOpen(false);
                        if (!singleFile) return;
                        const timestamp = Date.now();
                        const bucketFileName = `external_${timestamp}_${singleFile.name}`;
                        const resumeUrl = resumeObjectUrl(bucketFileName);
                        setLoading(true);
                        try {
                            await doSingleUpload(singleFile, bucketFileName, resumeUrl);
                        } finally {
                            setLoading(false);
                        }
                    }}
                    onCancel={() => setDuplicateDialogOpen(false)}
                />

                {/* Bulk duplicate dialog */}
                <Dialog open={bulkDuplicateDialogOpen} onClose={() => setBulkDuplicateDialogOpen(false)}>
                    <DialogTitle>Duplicate Files Found</DialogTitle>
                    <DialogContent>
                        <div>
                            The following files already exist:
                            <ul>
                                {bulkDuplicateFiles.map((n) => <li key={n}>{n}</li>)}
                            </ul>
                            Do you want to skip these files and upload the rest, upload all files anyway, or cancel?
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBulkDuplicateDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                setBulkDuplicateDialogOpen(false);
                                setLoading(true);
                                try {
                                    await doBulkUpload(bulkExtractedFiles, true);
                                    setBulkExtractedFiles([]);
                                    setBulkDuplicateFiles([]);
                                    setBulkFile(null);
                                    await fetchRecords();
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            Skip Duplicates
                        </Button>
                        <Button
                            variant="contained"
                            onClick={async () => {
                                setBulkDuplicateDialogOpen(false);
                                setLoading(true);
                                try {
                                    await doBulkUpload(bulkExtractedFiles, false);
                                    setBulkExtractedFiles([]);
                                    setBulkDuplicateFiles([]);
                                    setBulkFile(null);
                                    await fetchRecords();
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            Upload Anyway
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* The table spans the row. As a bare grid child it was 190px wide, which is why
                    every column header had truncated to two characters. */}
                {loading ? (
                    <div className="loading-placeholder">Loading...</div>
                ) : (
                    <Box className="cc-field--full">
                    <MintlyTable
                        rows={displayRecords}
                        selectors={['fileName', 'status', 'details', 'fileSize', 'createdAt', 'action', 'download', 'preview']}
                        /* The three action columns are the card's footer on a phone, not three
                           labelled facts, and the tablet table keeps Process/Retry in reach. */
                        columns={{
                            fileName: { card: 'title', tablet: true },
                            status: { card: 'status', tablet: true },
                            details: { card: 'subtitle', tablet: true },
                            action: { card: 'action', tablet: true },
                            download: { card: 'action' },
                            preview: { card: 'action' },
                        }}
                        tableHeadRow={{
                            fileName: 'File',
                            status: 'Status',
                            details: 'Details',
                            fileSize: 'Size',
                            createdAt: 'Uploaded At',
                            action: 'Action',
                            download: 'Download',
                            preview: 'Preview',
                        }}
                        tableBodyRow={{
                            fileName: (r: any) => (
                                <Tooltip title={r.fileName} placement="top">
                                    <Typography noWrap className="jsi-filename">{r.fileName}</Typography>
                                </Tooltip>
                            ),
                            status: (r: any) => formatStatus(r.status),
                            // Every outcome that resolved to a candidate links to them, not just
                            // SUCCESS. A REPLACED row overwrote that person's profile and a
                            // REJECTED one matched their account without writing — in both cases
                            // the operator's next question is "who?", and previously both showed
                            // a bare '-'. RecordLink renders a real anchor, so the row can be
                            // middle-clicked and copied like any other link.
                            details: (r: any) => {
                                const status = r.status?.toUpperCase();
                                if (status === UploadStatus.FAILED || status === UploadStatus.NEEDS_EMAIL) {
                                    return r.errorMessage || '-';
                                }
                                if (!r.jobSeekerId) return '-';
                                const name = r.jobSeekerName || r.jobSeekerId;
                                const link = <RecordLink to={`/jobseekers/${r.jobSeekerId}`}>{name}</RecordLink>;
                                return status === UploadStatus.REJECTED_EXISTING_USER
                                    ? <>{link} <Typography variant="caption" className="jsi-detail-note">already registered; CV not applied</Typography></>
                                    : link;
                            },
                            fileSize: (r: any) => formatFileSize(r.fileSize),
                            createdAt: (r: any) => formatDateTime(r.createdAt),
                            action: (r: any) => (
                                <>
                                    {r.status?.toUpperCase() === 'PENDING' && (
                                        r._id ? (
                                            processingIds.has(r._id)
                                                ? <CircularProgress size={20} />
                                                : <Button size="small" variant="outlined" onClick={() => processNow(r._id)}>Process Now</Button>
                                        ) : <Typography variant="body2">No id</Typography>
                                    )}
                                    {r.status?.toUpperCase() === 'FAILED' && r._id && (
                                        processingIds.has(r._id)
                                            ? <CircularProgress size={20} />
                                            : <Button size="small" variant="outlined" color="error" onClick={() => processNow(r._id)}>Retry</Button>
                                    )}
                                    {!['PENDING', 'FAILED'].includes(r.status?.toUpperCase()) && '-'}
                                </>
                            ),
                            download: (r: any) => (
                                <IconButton size="small" aria-label={`download-${r.fileName}`} onClick={async () => {
                                  if (!(await handleResumeClick(r.fileUrl))) {
                                    showToast('Could not open that resume. It may have been removed.', 'error');
                                  }
                                }}>
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            ),
                            preview: (r: any) => (
                                <IconButton size="small" aria-label={`preview-${r.fileName}`} onClick={async () => {
                                  /*
                                   * Signed before the dialog opens, not after. The viewer is a
                                   * Google iframe fetching the URL from Google's own servers, so a
                                   * private object renders as a blank frame with nothing on our
                                   * side to catch.
                                   */
                                  const signed = await resolveResumeUrl(r.fileUrl);
                                  if (!signed) {
                                    showToast('Could not open that resume. It may have been removed.', 'error');
                                    return;
                                  }
                                  setPreviewUrl(signed);
                                  setPreviewOpen(true);
                                }} disabled={!r.fileUrl}>
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                        pagination={{
                            enabled: true,
                            page,
                            rowsPerPage,
                            totalNumberOfRows: totalElements || records.length,
                            handleChangePage,
                        }}
                    />
                    </Box>
                )}

                <Dialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    fullWidth
                    maxWidth={false}
                    slotProps={{ paper: { sx: { width: '92vw', maxWidth: '92vw', height: '92vh', m: 'auto' } } }}
                >
                    <DialogTitle>{previewUrl ? previewUrl.split('/').pop() : 'Preview'}</DialogTitle>
                    <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
                        {previewUrl ? (
                            <div className="preview-iframe-wrapper" style={{ width: '100%', height: 'calc(92vh - 120px)' }}>
                                <iframe
                                    title="Resume Preview"
                                    src={`https://docs.google.com/gview?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                                    className="preview-iframe"
                                    style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                                />
                            </div>
                        ) : (
                            <div>No preview available.</div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </SectionCard>
        </Box>
    );
};

export default JobSeekersImportPage;
